import type { ServiceHealth, ServiceSummary } from '@apm/contracts';
import { navigate, routeHref } from '../router';
import { useLiveSelector } from '../store/useStream';
import { formatBytes, formatCount, formatDuration, formatPercent, formatRelative } from '../time';
import { Chip, EmptyState, ErrorText, Loading, Page, Section } from '../ui';
import { useServices } from './ServiceProvider';
import { setServiceFilter } from './service-filter';
import { instanceLine, lastSeenLine, sortServices } from './summary';

const HEALTH_LABEL: Record<ServiceHealth, string> = {
  ok: '정상',
  warning: '확인 필요',
  critical: '문제 있음',
};

const HEALTH_TONE: Record<ServiceHealth, 'ok' | 'fail' | 'error'> = {
  ok: 'ok',
  warning: 'fail',
  critical: 'error',
};

const HEALTH_BORDER: Record<ServiceHealth, string> = {
  ok: 'border-l-ok',
  warning: 'border-l-fail',
  critical: 'border-l-error',
};

/**
 * 서비스 카드를 누르면 실시간 화면으로 간다.
 *
 * 27회차에는 트레이스만 서비스를 요청 화면으로 보냈다. 그쪽 실시간 화면이 안내 한 줄뿐이라
 * 볼 것이 없었기 때문이다. 34회차부터 초당 요청·오류·응답시간이 그려져 갈 곳이 하나가 됐다.
 */
function openService(service: ServiceSummary): void {
  setServiceFilter(service.service);
  navigate({ name: 'live' });
}

export function ServicesPage() {
  const query = useServices();
  // 경보 목록만 본다 (D-C1)
  const liveAlerts = useLiveSelector((live) => live.alerts);
  const rows = sortServices(query.data?.rows ?? []);

  return (
    <Page>
      <Section
        title="서비스"
        note="서비스를 고르면 모든 화면이 그 서비스 기준으로 열린다. 문제 있는 서비스가 먼저 나온다"
        badge={query.data ? `${formatCount(rows.length)}개` : undefined}
        actions={
          <a
            href={routeHref({ name: 'live' })}
            onClick={(event) => {
              event.preventDefault();
              setServiceFilter('');
              navigate({ name: 'live' });
            }}
            className="text-sm text-link"
          >
            전체 보기
          </a>
        }
      >
        {query.loading && query.data === undefined ? <Loading /> : null}
        {query.error ? <ErrorText>{query.error}</ErrorText> : null}
        {!query.loading && !query.error && rows.length === 0 ? (
          <EmptyState
            title="아직 등록된 서비스가 없다"
            hint="요청을 한 번 보내거나 대상 앱의 에이전트와 관제 서버 주소를 확인할 것"
          />
        ) : null}
        {rows.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              <ServiceCard
                key={row.service}
                service={row}
                alertCount={liveAlerts.filter((alert) => alert.service === row.service).length}
              />
            ))}
          </div>
        ) : null}
      </Section>
    </Page>
  );
}

/**
 * 트레이스만 서비스가 조용한 것인가 (34회차).
 *
 * 스냅샷이 없는 앱은 요청이 곧 생존 신호라, 아무도 안 쓰면 인스턴스가 `stale`이 된다.
 * 그것은 신호가 끊긴 것이 아니라 요청이 없었다는 뜻이다(IIS 앱 풀은 유휴 20분이면 프로세스를
 * 내린다). 서버는 이것을 위험으로 보지 않고, 카드는 그 사실을 글자로 적는다.
 */
function isQuiet(service: ServiceSummary): boolean {
  return !service.realtime && service.instances.total > 0 && service.last5m.requests === 0;
}

function ServiceCard({ service, alertCount }: { service: ServiceSummary; alertCount: number }) {
  const storageBytes = service.storage.hotBytes + service.storage.coldBytes;
  const instances = instanceLine(service);
  // 이 서버가 아직 아무 요청도 못 받은 앱은 흐리게 그린다. 저장에만 기록이 남은 것도 같다
  const noSignal = service.lastSeenAt === undefined || service.lastSeenFromStorage === true;
  const card = (
    <article
      className={`h-full rounded-xl border border-hair border-l-4 bg-panel p-4 ${HEALTH_BORDER[service.health]} ${
        noSignal ? 'text-muted' : 'text-ink'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold" title={service.service}>
            {service.service}
          </h2>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <Chip tone={HEALTH_TONE[service.health]}>{HEALTH_LABEL[service.health]}</Chip>
            <Chip tone={service.realtime ? 'ok' : 'neutral'}>
              {service.realtime ? '실시간' : '트레이스만'}
            </Chip>
            {isQuiet(service) ? (
              <Chip
                tone="neutral"
                title="1초 보고를 보내지 않는 앱이라 요청이 곧 생존 신호다. 요청이 없는 것이지 끊긴 것이 아니다"
              >
                조용함
                {service.lastSeenAt === undefined || service.lastSeenFromStorage === true
                  ? ''
                  : ` · 마지막 요청 ${formatRelative(service.lastSeenAt)}`}
              </Chip>
            ) : null}
            {alertCount > 0 ? <Chip tone="error">경보 {formatCount(alertCount)}건</Chip> : null}
            {service.nameFromFolder ? (
              <Chip tone="neutral" title="저장 폴더에서 찾은 이름이라 조회 조건으로 쓰지 않는다">
                저장 폴더 이름
              </Chip>
            ) : null}
          </div>
        </div>
        <p className="shrink-0 text-xs text-muted">{lastSeenLine(service)}</p>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        {/* 트레이스만 인스턴스가 조용한 것은 요청이 없었다는 뜻이라 붉게 적지 않는다 (34회차) */}
        <Metric label="인스턴스" value={instances.label} warn={instances.warn} />
        <Metric label="최근 5분 요청" value={`${formatCount(service.last5m.requests)}건`} />
        <Metric
          label="오류율"
          value={formatPercent(service.last5m.errorRate)}
          warn={service.last5m.errorRate > 0}
        />
        <Metric label="응답시간(느린 5%)" value={formatDuration(service.last5m.p95Ms)} />
        <Metric label="저장 용량" value={formatBytes(storageBytes)} />
      </dl>
      {service.nameFromFolder ? (
        <p className="mt-4 text-xs text-muted">이름이 확정되지 않아 서비스 조건으로 열 수 없다.</p>
      ) : null}
    </article>
  );

  if (service.nameFromFolder) return card;
  return (
    <a
      href={routeHref({ name: 'live' })}
      onClick={(event) => {
        event.preventDefault();
        openService(service);
      }}
      className="block rounded-xl no-underline transition-transform hover:-translate-y-0.5"
      aria-label={`${service.service} 열기`}
    >
      {card}
    </a>
  );
}

function Metric({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className={`tabular mt-0.5 ${warn ? 'text-fail' : ''}`}>{value}</dd>
    </div>
  );
}
